'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { ApiService } from '../api/service';

/**
 * Callback invoked when an API request receives a 401 Unauthorised response.
 * Should attempt to refresh the session and return `true` if the original
 * request can be retried, or `false` if refresh failed (e.g. redirect to login).
 */
type OnUnauthorisedHandler = () => Promise<boolean>;

type ApiServiceContextValue = {
  apiService: ApiService;
  onUnauthorised?: OnUnauthorisedHandler;
};

const ApiServiceContext = createContext<ApiServiceContextValue | null>(null);

type ApiServiceProviderProps = {
  children: ReactNode;
  baseUrl: string;
  defaultCredentials?: RequestCredentials;
  onUnauthorised?: OnUnauthorisedHandler;
};

function ApiServiceProvider({ children, baseUrl, defaultCredentials, onUnauthorised }: ApiServiceProviderProps) {
  const apiService = ApiService.getInstance(baseUrl, defaultCredentials);

  const contextValue: ApiServiceContextValue = useMemo(
    () => ({
      apiService,
      onUnauthorised,
    }),
    [apiService, onUnauthorised]
  );

  return <ApiServiceContext.Provider value={contextValue}>{children}</ApiServiceContext.Provider>;
}

function useApiService(): ApiService {
  const context = useContext(ApiServiceContext);

  if (!context) {
    throw new Error('useApiService must be used within an ApiServiceProvider');
  }

  return context.apiService;
}

function useOnUnauthorised(): OnUnauthorisedHandler | undefined {
  const context = useContext(ApiServiceContext);
  return context?.onUnauthorised;
}

export { ApiServiceProvider, useApiService, useOnUnauthorised };
export type { OnUnauthorisedHandler };
